import type { Invoice } from "@shared/types";

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * How much of an invoice has been paid. An explicit `amountPaid` wins
 * (partial payments — deposits, progress payments); otherwise an invoice
 * marked "paid" counts as paid in full, so invoices from before amountPaid
 * existed stay correct. Clamped to [0, total].
 */
export function invoicePaid(inv: Pick<Invoice, "totalAmount" | "status" | "amountPaid">): number {
  const total = Number(inv.totalAmount) || 0;
  const paid = inv.amountPaid != null ? Number(inv.amountPaid) || 0 : inv.status === "paid" ? total : 0;
  return round2(Math.min(Math.max(paid, 0), total));
}

export function invoiceDue(inv: Pick<Invoice, "totalAmount" | "status" | "amountPaid">): number {
  return round2((Number(inv.totalAmount) || 0) - invoicePaid(inv));
}

/**
 * Plan category id → { invoiced, paid } across all invoice allocations.
 * A partially paid invoice spreads its payment across its allocations in
 * proportion to each allocation's share of the invoice total.
 */
export function invoiceTotalsByCategory(
  invoices: Invoice[]
): Record<number, { invoiced: number; paid: number }> {
  const out: Record<number, { invoiced: number; paid: number }> = {};
  for (const inv of invoices) {
    const total = Number(inv.totalAmount) || 0;
    const paidShare = total > 0 ? invoicePaid(inv) / total : 0;
    for (const a of inv.allocations ?? []) {
      const amt = Number(a.amount) || 0;
      const entry = out[a.budgetCategoryId] ?? (out[a.budgetCategoryId] = { invoiced: 0, paid: 0 });
      entry.invoiced += amt;
      entry.paid += amt * paidShare;
    }
  }
  for (const e of Object.values(out)) {
    e.invoiced = round2(e.invoiced);
    e.paid = round2(e.paid);
  }
  return out;
}
